/**
 * AudioManager - Handles all game audio (music and sound effects)
 */

import { eventBus, GameEvents } from '../core/EventBus.js';
import { STORAGE_KEYS } from '../core/Constants.js';

// Audio elements
let backgroundMusic = null;
let isMuted = false;
let masterVolume = 0.5;
let musicVolume = 0.5;
let sfxVolume = 0.7;

// Sound effect cache
const soundEffects = {};

/**
 * Initialize the audio manager
 */
export function initAudio() {
  // Get background music element
  backgroundMusic = document.getElementById('background-music');

  // Load saved settings
  loadAudioSettings();

  // Apply initial settings
  if (backgroundMusic) {
    backgroundMusic.volume = masterVolume * musicVolume;
    backgroundMusic.muted = isMuted;
  }

  // Set up audio controls
  setupAudioControls();

  // Preload sound effects
  preloadSoundEffects();
}

/**
 * Load audio settings from localStorage
 */
function loadAudioSettings() {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (saved) {
      const settings = JSON.parse(saved);
      if (settings.audio) {
        masterVolume = settings.audio.masterVolume ?? 0.5;
        musicVolume = settings.audio.musicVolume ?? 0.5;
        sfxVolume = settings.audio.sfxVolume ?? 0.7;
        isMuted = settings.audio.isMuted ?? false;
      }
    }
  } catch (error) {
    console.warn('Failed to load audio settings:', error);
  }
}

/**
 * Save audio settings to localStorage
 */
function saveAudioSettings() {
  try {
    let settings = {};
    const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (saved) {
      settings = JSON.parse(saved);
    }

    settings.audio = {
      masterVolume,
      musicVolume,
      sfxVolume,
      isMuted
    };

    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save audio settings:', error);
  }
}

/**
 * Set up audio control buttons
 */
function setupAudioControls() {
  const muteButton = document.getElementById('mute-button');
  const volumeUpButton = document.getElementById('volume-up');
  const volumeDownButton = document.getElementById('volume-down');

  if (muteButton) {
    muteButton.addEventListener('click', toggleMute);
    updateMuteButtonText();
  }

  if (volumeUpButton) {
    volumeUpButton.addEventListener('click', increaseVolume);
  }

  if (volumeDownButton) {
    volumeDownButton.addEventListener('click', decreaseVolume);
  }

  // Keyboard shortcuts
  document.addEventListener('keydown', (event) => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key) {
      case '+':
      case '=':
        increaseVolume();
        break;
      case '-':
      case '_':
        decreaseVolume();
        break;
      case 'm':
      case 'M':
        toggleMute();
        break;
    }
  });
}

/**
 * Preload sound effects for quick playback
 */
function preloadSoundEffects() {
  const sfxElements = {
    throwing: document.getElementById('throwing-sound'),
    caught: document.getElementById('caught-sound'),
    freakin: document.getElementById('freakin-sound'),
    freakin2: document.getElementById('freakin2-sound'),
    splashIntro: document.getElementById('splash-intro')
  };

  Object.entries(sfxElements).forEach(([name, element]) => {
    if (element) {
      soundEffects[name] = element;
      element.volume = masterVolume * sfxVolume;
    }
  });
}

/**
 * Play background music
 */
export function playMusic() {
  if (backgroundMusic && !isMuted) {
    backgroundMusic.play().catch(error => {
      console.log('Autoplay prevented:', error);
    });
  }
}

/**
 * Pause background music
 */
export function pauseMusic() {
  if (backgroundMusic) {
    backgroundMusic.pause();
  }
}

/**
 * Stop background music
 */
export function stopMusic() {
  if (backgroundMusic) {
    backgroundMusic.pause();
    backgroundMusic.currentTime = 0;
  }
}

/**
 * Play a sound effect
 * @param {string} name - Sound effect name
 * @param {boolean} allowOverlap - Allow multiple instances to play
 */
export function playSFX(name, allowOverlap = false) {
  const sound = soundEffects[name];
  if (!sound || isMuted) return;

  if (allowOverlap) {
    // Clone the audio for overlapping playback
    const clone = sound.cloneNode(true);
    clone.volume = masterVolume * sfxVolume;
    clone.play().catch(error => console.log(`Error playing ${name}:`, error));
    clone.onended = () => clone.remove();
  } else {
    sound.currentTime = 0;
    sound.volume = masterVolume * sfxVolume;
    sound.play().catch(error => console.log(`Error playing ${name}:`, error));
  }
}

/**
 * Play the throwing/casting sound
 */
export function playThrowSound() {
  playSFX('throwing');
}

/**
 * Play the fish caught sound
 */
export function playCaughtSound() {
  playSFX('caught', true);
}

/**
 * Play a failure sound (randomly selects between two variants)
 */
export function playFailSound() {
  const variant = Math.random() < 0.5 ? 'freakin' : 'freakin2';
  playSFX(variant);
}

/**
 * Play the splash intro sound
 */
export function playSplashIntro() {
  playSFX('splashIntro');
}

/**
 * Toggle mute state
 */
export function toggleMute() {
  isMuted = !isMuted;

  if (backgroundMusic) {
    backgroundMusic.muted = isMuted;
  }

  updateMuteButtonText();
  saveAudioSettings();
}

/**
 * Update mute button text
 */
function updateMuteButtonText() {
  const muteButton = document.getElementById('mute-button');
  if (muteButton) {
    muteButton.textContent = isMuted ? 'Unmute' : 'Mute';
  }
}

/**
 * Increase master volume
 */
export function increaseVolume() {
  masterVolume = Math.min(masterVolume + 0.1, 1.0);
  applyVolume();
  saveAudioSettings();
  console.log(`Volume: ${Math.round(masterVolume * 100)}%`);
}

/**
 * Decrease master volume
 */
export function decreaseVolume() {
  masterVolume = Math.max(masterVolume - 0.1, 0.0);
  applyVolume();
  saveAudioSettings();
  console.log(`Volume: ${Math.round(masterVolume * 100)}%`);
}

/**
 * Apply current volume to all audio
 */
function applyVolume() {
  if (backgroundMusic) {
    backgroundMusic.volume = masterVolume * musicVolume;
  }

  Object.values(soundEffects).forEach(sound => {
    if (sound) {
      sound.volume = masterVolume * sfxVolume;
    }
  });
}

/**
 * Set master volume
 * @param {number} volume - Volume level (0-1)
 */
export function setMasterVolume(volume) {
  masterVolume = Math.max(0, Math.min(1, volume));
  applyVolume();
  saveAudioSettings();
}

/**
 * Set music volume
 * @param {number} volume - Volume level (0-1)
 */
export function setMusicVolume(volume) {
  musicVolume = Math.max(0, Math.min(1, volume));
  applyVolume();
  saveAudioSettings();
}

/**
 * Set SFX volume
 * @param {number} volume - Volume level (0-1)
 */
export function setSFXVolume(volume) {
  sfxVolume = Math.max(0, Math.min(1, volume));
  applyVolume();
  saveAudioSettings();
}

/**
 * Get current volume settings
 */
export function getVolumeSettings() {
  return {
    master: masterVolume,
    music: musicVolume,
    sfx: sfxVolume,
    isMuted
  };
}

/**
 * Check if audio is muted
 */
export function isMutedState() {
  return isMuted;
}

export default {
  initAudio,
  playMusic,
  pauseMusic,
  stopMusic,
  playSFX,
  playThrowSound,
  playCaughtSound,
  playFailSound,
  playSplashIntro,
  toggleMute,
  increaseVolume,
  decreaseVolume,
  setMasterVolume,
  setMusicVolume,
  setSFXVolume,
  getVolumeSettings,
  isMutedState
};
